import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { Tooltip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import AccessTime from '@mui/icons-material/AccessTime'

import { NOTICES_TABLE_COLUMNS } from '../../constants/notices-table.constants'
import { TableUrlPagination } from '../../../../ui'
import { noticesDataPreparation } from '../../logics'
import { useNotificationsCalls } from '../../hooks'
import { PopupEditDialogComponent } from '../../dialogs'
import { useLoaderBasic } from '../../../loader/hooks'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const NoticesTableComponent = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const noticesLoader = useSelector((state) => state.loader.notices)
  const combos = useSelector((state) => state.combos)
  const { getAvisoById, updateAviso, deleteNotification, getAvisos } = useNotificationsCalls()

  const {
    state: { config },
    loadData,
  } = useLoaderBasic({ identifier: 'notices', call: getAvisos })

  const data = noticesLoader?.data?.registros || []
  const totalEntries = noticesLoader?.data?.total_registros
  const search = noticesLoader?.config?.params
  const loading = noticesLoader?.loading

  const [selectedAviso, setSelectedAviso] = useState({})
  const [openDialog, setOpenDialog] = useState(false)

  const formattedColumns = useMemo(() => {
    return NOTICES_TABLE_COLUMNS.map((column) => {
      const baseColumn = {
        accessorKey: column.field,
        header: formatMessage({ id: `pages.notices.table.columns.${column.field}.title` }),
        size: column.width,
        minSize: column.width,
        maxSize: column.width,
        muiTableBodyCellProps: {
          sx: {
            padding: '0 16px', // Estilo de padding base para todas las celdas
          },
        },
      }

      if (column.field === 'state') {
        return {
          ...baseColumn,
          muiTableBodyCellProps: {
            align: 'center',
            sx: { padding: 0 },
          },
          Cell: ({ cell }) => {
            const value = cell.getValue()
            if (value === 'active')
              return (
                <Tooltip title="Activa">
                  <CheckIcon sx={{ color: 'green' }} />
                </Tooltip>
              )
            if (value === 'inactive')
              return (
                <Tooltip title="Finalizada">
                  <CloseIcon sx={{ color: 'red' }} />
                </Tooltip>
              )
            if (value === 'ready')
              return (
                <Tooltip title="Programada">
                  <AccessTime color="secondary" />
                </Tooltip>
              )
            return '--'
          },
        }
      }

      if (column.comboId && combos[column.comboId]?.data) {
        const lookup = getLookup(combos[column.comboId].data)
        return {
          ...baseColumn,
          Cell: ({ cell }) => {
            const value = cell.getValue()
            return <span>{lookup[value] ?? value ?? '--'}</span>
          },
        }
      }

      return {
        ...baseColumn,
        Cell: ({ cell }) => <span>{cell.getValue() ?? '--'}</span>,
      }
    })
  }, [combos, formatMessage])

  const processedData = useMemo(() => noticesDataPreparation(data, { intl }), [data, intl])

  const finalTableOptions = useMemo(
    () => ({
      toolbar: false,
      selection: false,
      draggable: false,
    }),
    []
  )

  const openEditDialog = (event, rowData) => {
    getAvisoById(rowData.id).then((response) => {
      const data = response.data
      const aviso = {
        id: data.id,
        title: data.title,
        text: data.body,
        links: data.links ?? [],
        tags: data.tags ?? [],
        profilesSelected: data.target_users ?? [],
        startDate: data.start_date,
        endDate: data.end_date,
        created_at: data.creation_date,
        users_reached: data.users_reached,
        profilesList: combos['roles_all']?.data,
        ccaaList: combos['cod_zona_zeus']?.data,
        ccaa: data.ccaa ?? [],
        tagsList: combos['tags_avisos']?.data,
      }
      setSelectedAviso(aviso)
      setOpenDialog(true)
    })
  }

  const closeDialog = () => {
    setOpenDialog(false)
    setSelectedAviso({})
  }

  const updateNotice = (notice) => {
    updateAviso(selectedAviso.id, notice).then(() => {
      closeDialog()
      loadData(config)
    })
  }

  const deleteNotice = () => {
    console.log('ID del aviso a eliminar:', selectedAviso?.id)

    if (!selectedAviso?.id) {
      console.error('No se puede eliminar: el ID del aviso es undefined o nulo.')
      return
    }

    deleteNotification(selectedAviso.id)
      .then(() => {
        console.log('Aviso eliminado con éxito.')   
        closeDialog()
        loadData(config)
      })
      .catch((error) => {
        console.error('Error al intentar eliminar el aviso:', error)
      })
  }

  return (
    <>
      <TableUrlPagination
        columns={formattedColumns}
        options={finalTableOptions}
        totalEntries={totalEntries}
        search={search}
        data={processedData}
        isLoading={loading}
        onRowClick={openEditDialog}
      />
      <PopupEditDialogComponent
        open={openDialog}
        onClose={closeDialog}
        onEdit={updateNotice}
        onDelete={deleteNotice}
        {...selectedAviso}
      />
    </>
  )
}
